import React, { useState, useEffect, useCallback, useRef } from 'react';
import AttachmentGrid from './AttachmentGrid';
import ScreenRecorder from './ScreenRecorder';
import { VideoIcon } from './icons';
import './CaseAttachments.css';

/**
 * Case-level attachments panel (comment_id IS NULL). Reused by the end-user and
 * console detail pages; `api` supplies listAttachments/uploadAttachment/attachmentBlob.
 */
function CaseAttachments({ caseId, api, canUpload = false }) {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.listAttachments(caseId);
      setAttachments(res.data?.data ?? []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setAttachments([]);
    } finally {
      setLoading(false);
    }
  }, [api, caseId]);

  useEffect(() => { load(); }, [load]);

  // The app-level recorder uploads independently of this component's lifecycle;
  // refresh when it reports a new attachment (e.g. after the user returns here).
  useEffect(() => {
    const onAdded = () => load();
    window.addEventListener('support-case:attachment-added', onAdded);
    return () => window.removeEventListener('support-case:attachment-added', onAdded);
  }, [load]);

  const uploadOne = async (file) => {
    try {
      setUploading(true);
      setError(null);
      await api.uploadAttachment(caseId, file);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadOne(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (attId) => {
    try {
      setError(null);
      await api.deleteAttachment(caseId, attId);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Delete failed');
    }
  };

  return (
    <div className="case-attachments">
      <h2 className="case-attachments-title">Attachments</h2>

      {canUpload && (
        <div className="case-attachments-hint">
          <span className="case-attachments-hint-icon" aria-hidden><VideoIcon size={20} /></span>
          <div>
            <strong>Best way to report a bug</strong>
            <p>
              Use <b>Record screen</b> to capture your steps with voiceover — it shows us exactly how to
              reproduce the issue. You can also attach screenshots, logs, or files.
            </p>
          </div>
        </div>
      )}

      {canUpload && (
        <div className="case-attachments-actions">
          <ScreenRecorder uploader={(file) => api.uploadAttachment(caseId, file)} disabled={uploading} />
          <label className="case-attachment-upload-btn">
            {uploading ? 'Uploading…' : '+ Upload file'}
            <input ref={fileInputRef} type="file" onChange={handleFile} disabled={uploading} hidden />
          </label>
        </div>
      )}

      {error && <div className="case-attachments-error" role="alert">{error}</div>}

      {loading ? (
        <p className="case-attachments-empty">Loading…</p>
      ) : attachments.length === 0 ? (
        <p className="case-attachments-empty">No attachments{canUpload ? ' yet — upload a screenshot, log, or document.' : '.'}</p>
      ) : (
        <AttachmentGrid
          caseId={caseId}
          attachments={attachments}
          fetchBlob={api.attachmentBlob}
          onDelete={canUpload ? handleDelete : undefined}
        />
      )}
    </div>
  );
}

export default CaseAttachments;
